import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, Pencil, Trash2 } from "lucide-react";
import { api, errMsg } from "@/lib/api";
import { AdminTitle, Btn, Field, inputCls } from "@/pages/admin/Admin";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import NumInput from "@/components/NumInput";
import Promotions from "@/pages/admin/Promotions";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";

const TYPES = ["physical", "digital", "course", "workshop"];
const blank = { title: "", type: "percent", value: 10, scope: "all", product_ids: [], types: [], categories: [], active: true, starts_at: null, ends_at: null };
const toLocal = (s) => (s ? new Date(new Date(s).getTime() - new Date().getTimezoneOffset() * 60000).toISOString().slice(0, 16) : "");

function Checks({ options, value, onChange, testid }) {
  return (
    <div className="flex flex-wrap gap-3 max-h-48 overflow-y-auto border border-[#E6DFD5] p-3" data-testid={testid}>
      {options.map(([v, l]) => <label key={v} className="flex items-center gap-2 text-sm"><input type="checkbox" checked={value.includes(v)} onChange={(e) => onChange(e.target.checked ? [...value, v] : value.filter((x) => x !== v))} data-testid={`${testid}-${v}`} />{l}</label>)}
    </div>
  );
}

function DiscountForm({ initial, onSaved }) {
  const [f, setF] = useState(initial);
  const [products, setProducts] = useState([]);
  const [cols, setCols] = useState([]);
  useEffect(() => { api.get("/admin/products").then((r) => setProducts(r.data)); api.get("/collections").then((r) => setCols(r.data)); }, []);
  const save = async () => {
    try { f.discount_id ? await api.put(`/admin/discounts/${f.discount_id}`, f) : await api.post("/admin/discounts", f); toast.success("Discount saved"); onSaved(); }
    catch (e) { toast.error(errMsg(e)); }
  };
  const date = (k) => (e) => setF({ ...f, [k]: e.target.value ? new Date(e.target.value).toISOString() : null });
  return (
    <div className="space-y-4" data-testid="discount-form">
      <Field label="Title (internal)"><input value={f.title} onChange={(e) => setF({ ...f, title: e.target.value })} className={inputCls} data-testid="discount-title-input" /></Field>
      <div className="grid grid-cols-2 gap-3">
        <Field label="Type"><select value={f.type} onChange={(e) => setF({ ...f, type: e.target.value })} className={inputCls} data-testid="discount-type-select"><option value="percent">Percent (%)</option><option value="fixed">Fixed (IDR)</option></select></Field>
        <Field label="Value"><NumInput decimals={f.type === "percent" ? 2 : 0} value={f.value} onChange={(value) => setF((p) => ({ ...p, value }))} className={inputCls} data-testid="discount-value-input" /></Field>
      </div>
      <Field label="Apply to"><select value={f.scope} onChange={(e) => setF({ ...f, scope: e.target.value })} className={inputCls} data-testid="discount-scope-select">
        <option value="all">All products</option><option value="products">Specific products</option><option value="types">Product types</option><option value="categories">Collections / labels (Heritage, Essential...)</option></select></Field>
      {f.scope === "products" && <Checks testid="discount-products" options={products.map((p) => [p.product_id, p.name])} value={f.product_ids} onChange={(product_ids) => setF({ ...f, product_ids })} />}
      {f.scope === "types" && <Checks testid="discount-types" options={TYPES.map((t) => [t, t])} value={f.types} onChange={(types) => setF({ ...f, types })} />}
      {f.scope === "categories" && <Checks testid="discount-categories" options={cols.map((c) => [c.slug, c.title || c.slug])} value={f.categories} onChange={(categories) => setF({ ...f, categories })} />}
      <div className="grid grid-cols-2 gap-3">
        <Field label="Starts (optional)"><input type="datetime-local" value={toLocal(f.starts_at)} onChange={date("starts_at")} className={inputCls} data-testid="discount-start-input" /></Field>
        <Field label="Ends (optional)"><input type="datetime-local" value={toLocal(f.ends_at)} onChange={date("ends_at")} className={inputCls} data-testid="discount-end-input" /></Field>
      </div>
      <label className="flex items-center gap-2 text-sm"><Switch checked={f.active} onCheckedChange={(active) => setF({ ...f, active })} data-testid="discount-active-switch" />Active</label>
      <Btn onClick={save} disabled={!f.title || !f.value} data-testid="discount-save-btn">Save discount</Btn>
    </div>
  );
}

function AutoDiscounts() {
  const [rows, setRows] = useState([]);
  const [edit, setEdit] = useState(null);
  const load = () => api.get("/admin/discounts").then((r) => setRows(r.data));
  useEffect(() => { load(); }, []);
  const del = async (d) => { if (!window.confirm(`Delete discount "${d.title}"?`)) return; try { await api.delete(`/admin/discounts/${d.discount_id}`); load(); } catch (e) { toast.error(errMsg(e)); } };
  const toggle = async (d) => { try { await api.put(`/admin/discounts/${d.discount_id}`, { ...d, active: !d.active }); load(); } catch (e) { toast.error(errMsg(e)); } };
  const target = (d) => ({ all: "All products", products: `${d.product_ids.length} products`, types: d.types.join(", "), categories: d.categories.join(", ") }[d.scope]);
  return (
    <div className="space-y-6" data-testid="admin-discounts">
      <AdminTitle action={<Btn onClick={() => setEdit(blank)} className="flex items-center gap-2" data-testid="discount-create-btn"><Plus size={14} />New discount</Btn>}>Without coupon</AdminTitle>
      <p className="text-sm text-[#6E6259] -mt-4">Prices drop automatically (no code needed) and a discount label appears on the product photo. When several discounts match, the biggest saving is used.</p>
      <div className="bg-white border border-[#E6DFD5] divide-y divide-[#E6DFD5]">
        {rows.map((d) => (
          <div key={d.discount_id} className="p-4 flex flex-wrap items-center gap-4 text-sm" data-testid={`discount-row-${d.discount_id}`}>
            <span className="bg-[#9E2A2B] text-white px-2 py-1 text-xs">{d.type === "percent" ? `-${Number(d.value).toLocaleString("id-ID")}%` : `-Rp ${Number(d.value).toLocaleString("id-ID")}`}</span>
            <div className="flex-1 min-w-[200px]"><p className="font-medium">{d.title}</p><p className="text-xs text-[#6E6259]">{target(d)}{d.ends_at ? ` \u00b7 until ${new Date(d.ends_at).toLocaleString()}` : ""}</p></div>
            <Switch checked={d.active} onCheckedChange={() => toggle(d)} data-testid={`discount-toggle-${d.discount_id}`} />
            <button onClick={() => setEdit(d)} aria-label="Edit" data-testid={`discount-edit-${d.discount_id}`}><Pencil size={14} /></button>
            <button onClick={() => del(d)} aria-label="Delete" className="text-[#9E2A2B]" data-testid={`discount-delete-${d.discount_id}`}><Trash2 size={14} /></button>
          </div>
        ))}
        {!rows.length && <p className="p-6 text-sm text-[#6E6259]">No product discounts yet.</p>}
      </div>
      <Dialog open={!!edit} onOpenChange={(v) => !v && setEdit(null)}>
        <DialogContent className="rounded-none max-w-xl max-h-[90vh] overflow-y-auto">
          <DialogTitle className="font-serif text-3xl font-normal">{edit?.discount_id ? "Edit discount" : "New discount"}</DialogTitle>
          {edit && <DiscountForm initial={edit} onSaved={() => { setEdit(null); load(); }} />}
        </DialogContent>
      </Dialog>
    </div>
  );
}


export default function Discounts() {
  return (
    <div className="space-y-6" data-testid="admin-discount-hub">
      <h1 className="font-serif text-4xl" data-testid="discounts-hub-title">Discounts</h1>
      <p className="text-sm text-[#6E6259] -mt-4">Choose how a discount works: with a coupon code at checkout, or without a coupon (price drops automatically).</p>
      <Tabs defaultValue="coupon">
        <TabsList className="rounded-none bg-[#F6F2EE]">
          <TabsTrigger value="coupon" className="rounded-none" data-testid="discount-tab-coupon">With coupon</TabsTrigger>
          <TabsTrigger value="auto" className="rounded-none" data-testid="discount-tab-auto">Without coupon (price drops)</TabsTrigger>
        </TabsList>
        <TabsContent value="coupon" className="pt-6"><Promotions /></TabsContent>
        <TabsContent value="auto" className="pt-6"><AutoDiscounts /></TabsContent>
      </Tabs>
    </div>
  );
}
